import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useRest } from "@/composables/useRest";
import Vue from "vue";

export const useSitioStore = defineStore("sitio", () => {
  // ── Catálogo (usado por DatosLegalesTab, DenunciaFormDatosGenerales) ────
  const sitios = ref([]);
  const isLoaded = ref(false);

  const rest = useRest();
  const BASE_URL = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/tipo-sitio`;

  async function setSitios(refresh = false) {
    if (isLoaded.value && !refresh) return;

    try {
      let datos;

      if (!refresh && Vue.prototype.$offline.offlineServer) {
        const r = await Vue.prototype.$offline.getList("Sitio");
        if (Array.isArray(r.data.data) && r.data.data.length > 0 && r.data.data[0].list) {
          datos = r.data.data[0].list;
        }
      }

      if (!datos || refresh || datos.length < 1) {
        const r = await rest.get({ url: BASE_URL });
        datos = r.data.data;
        Vue.prototype.$offline.setList("Sitio", datos);
      }

      sitios.value = datos;
      isLoaded.value = true;
    } catch (e) {
      console.error("Error al cargar sitios:", e);
      throw e;
    }
  }

  function reset() {
    sitios.value = [];
    isLoaded.value = false;
  }

  // ── Parametrización CRUD con paginación ─────────────────────────────────
  const tipoSitios = ref([]);
  const allTipoSitios = ref([]);
  const allTipoSitiosLoaded = ref(false);
  const catalogNeedsRefresh = ref(false);
  const isLoading = ref(false);
  const pageSize = ref(6);
  const currentPage = ref(1);
  const totalTipoSitios = ref(0);

  const offset = computed(() => (currentPage.value - 1) * pageSize.value);
  const totalPages = computed(() => Math.ceil(totalTipoSitios.value / pageSize.value));

  async function getTotal(searchQuery = null) {
    try {
      let url = `${BASE_URL}?getTotal=true`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      const { data } = await rest.get({ url });
      totalTipoSitios.value = +data.doc.total;
    } catch (e) {
      console.error("getTotal>>", e);
    }
  }

  async function setAllTipoSitios(refresh = false) {
    try {
      if (allTipoSitiosLoaded.value && !refresh && !catalogNeedsRefresh.value) {
        return allTipoSitios.value;
      }
      const { data } = await rest.get({ url: BASE_URL });
      allTipoSitios.value = data.data;
      allTipoSitiosLoaded.value = true;
      catalogNeedsRefresh.value = false;
      return allTipoSitios.value;
    } catch (e) {
      console.error("setAllTipoSitios>>", e);
      return allTipoSitios.value;
    }
  }

  async function setTipoSitios(page = 1) {
    try {
      isLoading.value = true;
      currentPage.value = page;
      const url = `${BASE_URL}?limit=${pageSize.value}&offset=${(page - 1) * pageSize.value}`;
      const { data } = await rest.get({ url });
      tipoSitios.value = data.data;
    } catch (e) {
      console.error("setTipoSitios>>", e);
    } finally {
      isLoading.value = false;
    }
  }

  async function searchTipoSitios(searchQuery, page = 1) {
    try {
      isLoading.value = true;
      currentPage.value = page;
      const url = `${BASE_URL}?limit=${pageSize.value}&offset=${(page - 1) * pageSize.value}&search=${encodeURIComponent(searchQuery)}`;
      const { data } = await rest.get({ url });
      tipoSitios.value = data.data;
    } catch (e) {
      console.error("searchTipoSitios>>", e);
    } finally {
      isLoading.value = false;
    }
  }

  async function createTipoSitio(payload) {
    try {
      const { data } = await rest.post({ url: BASE_URL, formData: payload });
      allTipoSitiosLoaded.value = false;
      isLoaded.value = false;
      catalogNeedsRefresh.value = true;
      return data;
    } catch (e) {
      console.error("createTipoSitio>>", e);
      throw e;
    }
  }

  async function updateTipoSitio(id, payload) {
    try {
      const { data } = await rest.put({ url: `${BASE_URL}/${id}`, formData: payload });
      allTipoSitiosLoaded.value = false;
      isLoaded.value = false;
      catalogNeedsRefresh.value = true;
      return data;
    } catch (e) {
      console.error("updateTipoSitio>>", e);
      throw e;
    }
  }

  async function deleteTipoSitio(id) {
    try {
      const { data } = await rest.del({ url: `${BASE_URL}/${id}` });
      allTipoSitiosLoaded.value = false;
      isLoaded.value = false;
      catalogNeedsRefresh.value = true;
      await getTotal();
      await setTipoSitios(currentPage.value);
      return data;
    } catch (e) {
      console.error("deleteTipoSitio>>", e);
      throw e;
    }
  }

  function handlePageChange(event) {
    goToPage(Math.floor(event.first / pageSize.value) + 1);
  }

  function goToPage(page) {
    currentPage.value = page;
    setTipoSitios(page);
  }

  function prevPage() {
    if (currentPage.value > 1) goToPage(currentPage.value - 1);
  }

  function nextPage() {
    if (currentPage.value < totalPages.value) goToPage(currentPage.value + 1);
  }

  return {
    // Catálogo
    sitios,
    isLoaded,
    setSitios,
    reset,
    // Parametrización
    tipoSitios,
    allTipoSitios,
    isLoading,
    pageSize,
    currentPage,
    offset,
    totalTipoSitios,
    totalPages,
    setAllTipoSitios,
    setTipoSitios,
    searchTipoSitios,
    getTotal,
    createTipoSitio,
    updateTipoSitio,
    deleteTipoSitio,
    handlePageChange,
    goToPage,
    prevPage,
    nextPage,
  };
});
